<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { setNewPassword } from '../../../api/auth';
import { mapGetters } from 'vuex';

export default {
  props: {
    resetPasswordToken: { type: String, default: '' },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        confirmPassword: '',
        password: '',
      },
      newPasswordAPI: {
        message: '',
        showLoading: false,
      },
      error: '',
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
  },
  mounted() {
    if (!this.resetPasswordToken) {
      window.location = DEFAULT_REDIRECT_URL;
    }
  },
  validations: {
    credentials: {
      password: {
        required,
        minLength: minLength(6),
      },
      confirmPassword: {
        required,
        minLength: minLength(6),
        isEqPassword(value) {
          if (value !== this.credentials.password) {
            return false;
          }
          return true;
        },
      },
    },
  },
  methods: {
    showAlertMessage(message) {
      this.newPasswordAPI.showLoading = false;
      useAlert(message);
    },
    submitForm() {
      this.newPasswordAPI.showLoading = true;
      const credentials = {
        confirmPassword: this.credentials.confirmPassword,
        password: this.credentials.password,
        resetPasswordToken: this.resetPasswordToken,
      };
      setNewPassword(credentials)
        .then(() => {
          window.location = DEFAULT_REDIRECT_URL;
        })
        .catch(error => {
          this.showAlertMessage(
            error?.message || this.$t('SET_NEW_PASSWORD.API.ERROR_MESSAGE')
          );
        });
    },
  },
};
</script>

<template>
  <div class="sp-auth-page">
    <!-- Left visual panel -->
    <div class="sp-auth-visual">
      <div class="sp-grid-bg" />
      <div class="sp-glow-orb sp-glow-1" />
      <div class="sp-glow-orb sp-glow-2" />
      <div class="sp-glow-orb sp-glow-3" />
      <div class="sp-particle sp-particle-1" />
      <div class="sp-particle sp-particle-2" />
      <div class="sp-particle sp-particle-3" />
      <div class="sp-particle sp-particle-4" />
      <div class="sp-particle sp-particle-5" />

      <div class="sp-visual-inner">
        <div class="sp-animate-in" style="margin-bottom: 16px">
          <div class="sp-badge">
            <span class="sp-badge-dot" />
            Almost there
          </div>
        </div>

        <div
          class="sp-animate-in sp-delay-1"
          style="text-align: center; margin-bottom: 28px"
        >
          <h1 class="sp-hero-text" style="font-size: 52px">
            New<br />
            <span class="sp-gradient-text">Password.</span>
          </h1>
          <p class="sp-hero-sub">
            Choose a strong password to keep your account secure.
          </p>
        </div>

        <!-- Password tips -->
        <div
          class="sp-animate-in sp-delay-2"
          style="
            width: 100%;
            max-width: 380px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          "
        >
          <div
            class="sp-glass-card"
            style="
              padding: 14px 18px;
              display: flex;
              align-items: center;
              gap: 14px;
            "
          >
            <div
              class="sp-feature-icon"
              style="
                width: 38px;
                height: 38px;
                flex-shrink: 0;
                background: linear-gradient(135deg, #6366f1, #8b5cf6);
              "
            >
              <svg
                width="17"
                height="17"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div style="font-size: 12px; color: #475569; line-height: 1.5">
              Use at least
              <strong style="color: #0f172a">6 characters</strong> with a mix of
              letters, numbers and symbols.
            </div>
          </div>
          <div
            class="sp-glass-card"
            style="
              padding: 14px 18px;
              display: flex;
              align-items: center;
              gap: 14px;
            "
          >
            <div
              class="sp-feature-icon"
              style="
                width: 38px;
                height: 38px;
                flex-shrink: 0;
                background: linear-gradient(135deg, #8b5cf6, #a855f7);
              "
            >
              <svg
                width="17"
                height="17"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <div style="font-size: 12px; color: #475569; line-height: 1.5">
              Avoid using the same password across multiple sites and services.
            </div>
          </div>
          <div
            class="sp-glass-card"
            style="
              padding: 14px 18px;
              display: flex;
              align-items: center;
              gap: 14px;
            "
          >
            <div
              class="sp-feature-icon"
              style="
                width: 38px;
                height: 38px;
                flex-shrink: 0;
                background: linear-gradient(135deg, #6366f1, #a855f7);
              "
            >
              <svg
                width="17"
                height="17"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <div style="font-size: 12px; color: #475569; line-height: 1.5">
              Consider using a
              <strong style="color: #0f172a">password manager</strong> to
              generate and store unique passwords.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right form panel -->
    <div class="sp-auth-form-side">
      <div class="sp-auth-form-inner-wrap">
        <div class="sp-auth-form-container">
          <!-- Logo -->
          <div
            class="sp-animate-in"
            style="text-align: center; margin-bottom: 24px"
          >
            <img
              :src="globalConfig.logo"
              :alt="globalConfig.installationName"
              style="height: 56px; display: inline-block"
              class="dark:hidden"
            />
            <img
              v-if="globalConfig.logoDark"
              :src="globalConfig.logoDark"
              :alt="globalConfig.installationName"
              style="height: 56px; display: none"
              class="dark:inline-block"
            />
          </div>

          <!-- Key icon -->
          <div class="sp-animate-in sp-delay-1" style="margin-bottom: 20px">
            <div
              style="
                width: 56px;
                height: 56px;
                background: linear-gradient(135deg, #ede9fe, #ddd6fe);
                border-radius: 18px;
                display: flex;
                align-items: center;
                justify-content: center;
              "
            >
              <svg
                width="26"
                height="26"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#6366f1"
                stroke-width="1.5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
          </div>

          <div class="sp-animate-in sp-delay-2" style="margin-bottom: 24px">
            <h2
              style="
                font-size: 22px;
                font-weight: 800;
                color: #0f172a;
                letter-spacing: -0.03em;
                margin-bottom: 6px;
              "
            >
              {{ $t('SET_NEW_PASSWORD.TITLE') }}
            </h2>
            <p style="font-size: 13px; color: #64748b">
              Choose a Secure Password for Your Supportly Account.
            </p>
          </div>

          <form class="sp-animate-in sp-delay-3" @submit.prevent="submitForm">
            <div class="sp-field">
              <label class="sp-label">{{
                $t('SET_NEW_PASSWORD.PASSWORD.LABEL')
              }}</label>
              <input
                v-model="credentials.password"
                name="password"
                type="password"
                class="sp-input"
                :class="{ 'sp-input-error': v$.credentials.password.$error }"
                :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
                @blur="v$.credentials.password.$touch"
              />
              <p v-if="v$.credentials.password.$error" class="sp-error-msg">
                {{ $t('SET_NEW_PASSWORD.PASSWORD.ERROR') }}
              </p>
            </div>
            <div class="sp-field" style="margin-top: 12px">
              <label class="sp-label">{{
                $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.LABEL')
              }}</label>
              <input
                v-model="credentials.confirmPassword"
                name="confirm_password"
                type="password"
                class="sp-input"
                :class="{
                  'sp-input-error': v$.credentials.confirmPassword.$error,
                }"
                :placeholder="
                  $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.PLACEHOLDER')
                "
                @blur="v$.credentials.confirmPassword.$touch"
              />
              <p
                v-if="v$.credentials.confirmPassword.$error"
                class="sp-error-msg"
              >
                {{ $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.ERROR') }}
              </p>
            </div>
            <button
              type="submit"
              class="sp-btn"
              style="margin-top: 16px"
              :disabled="
                v$.credentials.password.$invalid ||
                v$.credentials.confirmPassword.$invalid ||
                newPasswordAPI.showLoading
              "
            >
              <span v-if="!newPasswordAPI.showLoading">{{
                $t('SET_NEW_PASSWORD.SUBMIT')
              }}</span>
              <span v-else>Updating...</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes sp-particle-float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
  25% {
    transform: translate(20px, -30px) scale(1.5);
    opacity: 0.8;
  }
  50% {
    transform: translate(-10px, -60px) scale(1);
    opacity: 0.6;
  }
  75% {
    transform: translate(15px, -20px) scale(1.3);
    opacity: 0.9;
  }
}
@keyframes sp-float-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -20px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 15px) scale(0.95);
  }
}
@keyframes sp-slide-in {
  from {
    opacity: 0;
    transform: translateY(32px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes sp-pulse-soft {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
@keyframes sp-shimmer-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.sp-auth-page {
  display: flex;
  height: 100vh;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
}
.sp-auth-visual {
  display: none;
  width: 50%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    145deg,
    #f5f3ff 0%,
    #ede9fe 25%,
    #e0e7ff 50%,
    #ddd6fe 75%,
    #f0f9ff 100%
  );
}
@media (min-width: 1024px) {
  .sp-auth-visual {
    display: flex;
  }
}
.sp-auth-form-side {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
  background: #ffffff;
}
.sp-auth-form-inner-wrap {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  box-sizing: border-box;
}
@media (min-width: 1024px) {
  .sp-auth-form-inner-wrap {
    padding: 32px 48px;
  }
}
@media (min-width: 1280px) {
  .sp-auth-form-inner-wrap {
    padding: 32px 64px;
  }
}
.sp-auth-form-container {
  width: 100%;
  max-width: 420px;
}
.sp-grid-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(99, 102, 241, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}
.sp-glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
}
.sp-glow-1 {
  width: 500px;
  height: 500px;
  background: rgba(139, 92, 246, 0.3);
  top: -120px;
  right: -100px;
  animation: sp-float-drift 8s ease-in-out infinite;
}
.sp-glow-2 {
  width: 450px;
  height: 450px;
  background: rgba(99, 102, 241, 0.25);
  bottom: -60px;
  left: -80px;
  animation: sp-float-drift 10s ease-in-out infinite reverse;
}
.sp-glow-3 {
  width: 300px;
  height: 300px;
  background: rgba(59, 130, 246, 0.2);
  top: 35%;
  left: 25%;
  animation: sp-float-drift 12s ease-in-out infinite 2s;
}
.sp-particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.4);
  pointer-events: none;
}
.sp-particle-1 {
  width: 6px;
  height: 6px;
  top: 15%;
  left: 20%;
  animation: sp-particle-float 6s ease-in-out infinite;
}
.sp-particle-2 {
  width: 4px;
  height: 4px;
  top: 35%;
  right: 15%;
  animation: sp-particle-float 8s ease-in-out infinite 1s;
}
.sp-particle-3 {
  width: 8px;
  height: 8px;
  bottom: 25%;
  left: 30%;
  animation: sp-particle-float 7s ease-in-out infinite 2s;
}
.sp-particle-4 {
  width: 5px;
  height: 5px;
  top: 60%;
  right: 25%;
  animation: sp-particle-float 9s ease-in-out infinite 0.5s;
}
.sp-particle-5 {
  width: 3px;
  height: 3px;
  top: 80%;
  left: 15%;
  animation: sp-particle-float 5s ease-in-out infinite 3s;
}
.sp-visual-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 28px 36px;
}
.sp-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  border: 1px solid rgba(99, 102, 241, 0.15);
  backdrop-filter: blur(12px);
}
.sp-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  animation: sp-pulse-soft 2s infinite;
}
.sp-hero-text {
  font-size: 60px;
  font-weight: 900;
  line-height: 1;
  color: #0f172a;
  letter-spacing: -0.04em;
  margin-bottom: 10px;
}
.sp-hero-sub {
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
  max-width: 340px;
  margin: 0 auto;
  text-align: center;
}
.sp-gradient-text {
  background: linear-gradient(135deg, #4f46e5, #7c3aed, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.sp-glass-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.sp-feature-icon {
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sp-animate-in {
  animation: sp-slide-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0;
}
.sp-delay-1 {
  animation-delay: 0.1s;
}
.sp-delay-2 {
  animation-delay: 0.2s;
}
.sp-delay-3 {
  animation-delay: 0.3s;
}
.sp-field {
  display: flex;
  flex-direction: column;
}
.sp-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}
.sp-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
}
.sp-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
}
.sp-input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}
.sp-input-error {
  border-color: #f43f5e;
  box-shadow: 0 0 0 4px rgba(244, 63, 94, 0.1);
}
.sp-error-msg {
  font-size: 12px;
  color: #f43f5e;
  margin-top: 4px;
}
.sp-btn {
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}
.sp-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.45);
}
.sp-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}
.sp-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.15),
    transparent
  );
  animation: sp-shimmer-slide 3s infinite;
}
</style>
